import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Play, RefreshCw, ExternalLink, CheckCircle2, XCircle, Clock, AlertTriangle, Inbox, Pencil, Plus, X, Loader2, Database } from "lucide-react";

const STATUS_CFG = {
    success: { icon: CheckCircle2, cls: "text-green-700 bg-green-100", label: "sucesso" },
    partial: { icon: AlertTriangle, cls: "text-amber-700 bg-amber-100", label: "parcial" },
    failed:  { icon: XCircle, cls: "text-red-700 bg-red-100", label: "falha" },
    running: { icon: Clock, cls: "text-blue-700 bg-blue-100 animate-pulse", label: "executando" },
};

export default function AdminScrapers() {
    const [rows, setRows] = useState([]);
    const [runningId, setRunningId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [totalPending, setTotalPending] = useState(0);

    // Edit/New modal state
    const [editRow, setEditRow] = useState(null);       // { brand_id, name, urls } for edit
    const [newOpen, setNewOpen] = useState(false);
    const [form, setForm] = useState({ name: "", urls: "" });
    const [saving, setSaving] = useState(false);
    const [migrating, setMigrating] = useState(false);

    const load = async () => {
        const { data } = await api.get("/admin/scrapers");
        setRows(data);
        setTotalPending(data.reduce((s, r) => s + (r.pending_review || 0), 0));
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const runOne = async (row) => {
        if (!row.url_count) { toast.error("Sem URL configurada"); return; }
        setRunningId(row.brand_id);
        toast.info(`Rodando scraper de ${row.brand_name}…`);
        try {
            const { data } = await api.post(`/admin/scrapers/${row.brand_id}/run`);
            toast.success(`${row.brand_name}: ${data.products_inserted} produto(s) na fila de curadoria`);
            load();
        } catch (e) {
            toast.error(`${row.brand_name}: ${formatApiErrorDetail(e.response?.data?.detail) || "falha"}`);
        } finally { setRunningId(null); }
    };

    const openEdit = (row) => {
        setEditRow(row);
        setForm({ name: row.brand_name, urls: (row.urls || []).join("\n") });
    };

    const openNew = () => {
        setNewOpen(true);
        setForm({ name: "", urls: "" });
    };

    const closeModal = () => {
        setEditRow(null);
        setNewOpen(false);
        setForm({ name: "", urls: "" });
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Informe o nome"); return; }
        const urlList = form.urls.split("\n").map((u) => u.trim()).filter(Boolean);
        setSaving(true);
        try {
            await api.put(`/admin/brands/${editRow.brand_id}`, {
                name: form.name.trim(),
                scraper_urls: urlList,
            });
            toast.success("Alterações salvas");
            closeModal();
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao salvar");
        } finally { setSaving(false); }
    };

    const saveNew = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Informe o nome"); return; }
        const urlList = form.urls.split("\n").map((u) => u.trim()).filter(Boolean);
        setSaving(true);
        try {
            await api.post("/admin/brands", {
                name: form.name.trim(),
                scraper_urls: urlList,
            });
            toast.success(`Empresa "${form.name.trim()}" cadastrada`);
            closeModal();
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao cadastrar");
        } finally { setSaving(false); }
    };

    const runMigrate = async () => {
        if (!window.confirm("Migrar URLs do arquivo Python para o banco? (Ação idempotente — só grava marcas ainda não migradas)")) return;
        setMigrating(true);
        try {
            const { data } = await api.post("/admin/scrapers/migrate-urls");
            toast.success(`${data.migrated} marca(s) migrada(s) · ${data.skipped_already_set} já estavam no banco`);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na migração");
        } finally { setMigrating(false); }
    };

    return (
        <div className="p-8">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">Fase 8 · Scrapers</div>
                    <h1 className="mt-1 font-heading font-black text-3xl tracking-tight">Coleta automatizada</h1>
                    <p className="mt-2 text-sm text-neutral-500 max-w-2xl">
                        Playwright renderiza cada site + Gemini 3 Flash extrai produtos. Curadoria manual antes de publicar. Agendamento mensal (dia 1, 03:00 UTC).
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={openNew} data-testid="new-company-btn" className="h-11 px-4 bg-brand-blue text-white font-heading font-bold uppercase tracking-wider text-sm hover:bg-brand-blue-hover flex items-center gap-2">
                        <Plus size={14} /> Nova empresa
                    </button>
                    <a href="/admin/scrapers/curadoria" className="h-11 px-4 bg-brand-yellow text-ink font-heading font-bold uppercase tracking-wider text-sm hover:bg-brand-yellow-hover flex items-center gap-2" data-testid="link-curadoria">
                        <Inbox size={16} /> Curadoria ({totalPending})
                    </a>
                    <button onClick={load} className="h-11 px-4 bg-neutral-100 hover:bg-neutral-200 text-sm font-medium flex items-center gap-2">
                        <RefreshCw size={14} /> Atualizar
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="mt-8 font-mono text-sm text-neutral-500">carregando…</div>
            ) : (
                <>
                    <div className="mt-6 grid grid-cols-3 gap-4">
                        <Stat label="Marcas configuradas" value={rows.filter((r) => r.url_count > 0).length} sub={`${rows.filter((r) => !r.url_count).length} sem URL`} />
                        <Stat label="Total em curadoria" value={totalPending} accent />
                        <Stat label="Frequência" value="Mensal" sub="Dia 1 · 03h UTC" />
                    </div>

                    <div className="mt-8 bg-white border border-neutral-300 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-neutral-50 font-mono text-xs uppercase tracking-widest text-neutral-500">
                                <tr>
                                    <th className="text-left p-3">Marca</th>
                                    <th className="text-left p-3">URL</th>
                                    <th className="text-left p-3">Última execução</th>
                                    <th className="text-left p-3">Status</th>
                                    <th className="text-left p-3">Achados</th>
                                    <th className="text-left p-3">Curadoria</th>
                                    <th className="text-right p-3">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => {
                                    const st = STATUS_CFG[r.last_run?.status] || null;
                                    return (
                                        <tr key={r.brand_id} className={`border-t border-neutral-200 ${i % 2 ? "bg-neutral-50/50" : ""}`} data-testid={`scraper-row-${r.brand_id}`}>
                                            <td className="p-3 font-medium">{r.brand_name}</td>
                                            <td className="p-3">
                                                {r.urls[0] ? (
                                                    <a href={r.urls[0]} target="_blank" rel="noreferrer" className="text-brand-blue hover:underline font-mono text-xs inline-flex items-center gap-1">
                                                        <ExternalLink size={11} /> {r.urls[0].replace(/^https?:\/\//, "").slice(0, 42)}{r.urls[0].length > 42 ? "…" : ""}
                                                    </a>
                                                ) : <span className="text-neutral-400 text-xs font-mono">sem URL</span>}
                                                {r.urls.length > 1 && <span className="ml-2 text-[10px] font-mono text-neutral-500">+{r.urls.length - 1}</span>}
                                            </td>
                                            <td className="p-3 font-mono text-xs text-neutral-500">
                                                {r.last_run ? new Date(r.last_run.started_at).toLocaleString("pt-BR") : "—"}
                                            </td>
                                            <td className="p-3">
                                                {st ? (
                                                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest ${st.cls}`}>
                                                        <st.icon size={12} /> {st.label}
                                                    </span>
                                                ) : <span className="text-neutral-400 text-xs">—</span>}
                                            </td>
                                            <td className="p-3 font-mono text-sm">{r.last_run?.products_found ?? "—"}</td>
                                            <td className="p-3">
                                                {r.pending_review > 0 ? (
                                                    <span className="px-2 py-1 bg-brand-yellow text-ink text-xs font-mono font-bold">{r.pending_review}</span>
                                                ) : <span className="text-neutral-400 text-xs font-mono">0</span>}
                                            </td>
                                            <td className="p-3">
                                                <div className="flex justify-end gap-1">
                                                    <button
                                                        data-testid={`edit-scraper-${r.brand_id}`}
                                                        onClick={() => openEdit(r)}
                                                        className="h-9 w-9 border border-neutral-300 hover:bg-neutral-50 grid place-items-center"
                                                        title="Editar empresa e URLs"
                                                    >
                                                        <Pencil size={12} />
                                                    </button>
                                                    <button
                                                        data-testid={`run-scraper-${r.brand_id}`}
                                                        disabled={runningId === r.brand_id || !r.url_count}
                                                        onClick={() => runOne(r)}
                                                        className="h-9 px-3 bg-brand-blue text-white text-xs font-heading font-bold uppercase tracking-wider hover:bg-brand-blue-hover disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                                                    >
                                                        {runningId === r.brand_id ? <><Clock size={12} className="animate-spin" /> …</> : <><Play size={12} /> Executar</>}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6 flex items-center justify-end">
                        <button
                            onClick={runMigrate}
                            disabled={migrating}
                            data-testid="migrate-urls-btn"
                            className="text-xs font-mono text-neutral-500 hover:text-brand-blue flex items-center gap-1.5 disabled:opacity-50"
                            title="Copia URLs do arquivo Python para o banco (idempotente)"
                        >
                            {migrating ? <Loader2 size={11} className="animate-spin" /> : <Database size={11} />}
                            {migrating ? "migrando…" : "Migrar URLs hardcoded → banco"}
                        </button>
                    </div>
                </>
            )}

            {/* EDIT / NEW MODAL */}
            {(editRow || newOpen) && (
                <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="scraper-modal" onClick={() => !saving && closeModal()}>
                    <form onSubmit={editRow ? saveEdit : saveNew} onClick={(e) => e.stopPropagation()} className="bg-white max-w-xl w-full border-2 border-brand-blue">
                        <div className="p-5 border-b border-neutral-200 flex items-start justify-between">
                            <div>
                                <div className="text-xs font-mono uppercase tracking-widest text-brand-blue">// {editRow ? "editar" : "cadastrar"}</div>
                                <h3 className="mt-1 font-heading font-black text-2xl tracking-tight">
                                    {editRow ? "Editar empresa" : "Nova empresa"}
                                </h3>
                            </div>
                            <button type="button" onClick={closeModal} disabled={saving} className="text-neutral-400 hover:text-neutral-900"><X size={16} /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <label className="block">
                                <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">Nome da empresa *</span>
                                <input
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    required
                                    autoFocus={!editRow}
                                    data-testid="scraper-modal-name"
                                    placeholder="Ex: Nova Fabricante"
                                    className="mt-1 w-full h-11 px-3 border border-neutral-300 focus:outline-none focus:border-brand-blue"
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">URL(s) de catálogo — uma por linha</span>
                                <textarea
                                    value={form.urls}
                                    onChange={(e) => setForm({ ...form, urls: e.target.value })}
                                    rows={4}
                                    data-testid="scraper-modal-urls"
                                    placeholder="https://www.exemplo.com/produtos&#10;https://www.exemplo.com/catalogo"
                                    className="mt-1 w-full px-3 py-2 border border-neutral-300 focus:outline-none focus:border-brand-blue font-mono text-xs"
                                />
                                <div className="mt-1 text-[11px] font-mono text-neutral-500">Deixe vazio para cadastrar a marca sem scraping automático.</div>
                            </label>
                        </div>
                        <div className="p-5 border-t border-neutral-200 flex items-center justify-end gap-2">
                            <button type="button" onClick={closeModal} disabled={saving} className="h-10 px-4 border border-neutral-300 text-sm hover:bg-neutral-50">Cancelar</button>
                            <button type="submit" disabled={saving} data-testid="scraper-modal-save" className="h-10 px-5 bg-brand-blue text-white text-sm font-heading font-bold uppercase tracking-wider hover:bg-brand-blue-hover disabled:opacity-60 flex items-center gap-2">
                                {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                                {editRow ? "Salvar" : "Cadastrar"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

const Stat = ({ label, value, sub, accent }) => (
    <div className={`bg-white border border-neutral-300 p-5 ${accent ? "border-brand-yellow border-2" : ""}`}>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">{label}</div>
        <div className="mt-2 font-heading font-black text-3xl tracking-tight">{value}</div>
        {sub && <div className="mt-1 text-xs text-neutral-500 font-mono">{sub}</div>}
    </div>
);
